<script setup lang="ts">
    import { inject, ref, computed, onMounted, reactive, toRaw } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import axios from 'axios';
    import ImportMembresComponent from './@components/ImportMembresComponent.vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import ToastNotification from '@/components/ToastNotification.vue';
    import CustomPagination from '@/components/CustomPagination.vue';
    import { formatAmount, formatDate } from '@/utils';


    const route = useRoute();
    const router = useRouter();
    const BACKEND_URL = inject('BACKEND_URL');
    const collectivite = ref<any>(route.meta.item || {});
    const refreshMembers = ref(0);
    const usersCount = ref(0);
    const membersList = ref<any[]>([]);
    const loading = ref(false);
    const loadingMembers = ref(false);
    const loadingInvitations = ref(false);
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info' | 'warning'>('success');
    const showToast = ref(false);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const loadingPromoteGerant = ref<{[key: number]: boolean}>({});
    const loadingSendInvitation = ref<{[key: number]: boolean}>({});
    const activeTab = ref('information');
    const selectedMembers = ref<number[]>([]);
    const currentUser = ref<any>(null);

    // Gérant
    const gerantMode = ref<'view' | 'edit' | 'replace'>('view');
    const gerantReplaceMode = ref<'existing' | 'new'>('new');
    const gerantFormData = reactive({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        date_naissance: '' as string | null,
        age: 0,
    });
    const gerantReplaceExistingId = ref<number | null>(null);
    const availableUsersForGerant = ref<any[]>([]);
    const loadingGerantAction = ref(false);
    const loadingResendInvitation = ref(false);
    const gerantErrors = ref<Record<string, string[]>>({});

    // États collapsables des sections
    const isCollectiviteOpen = ref(true);
    const isWalletOpen = ref(true);
    const isGerantSectionOpen = ref(true);
    const isMembresOpen = ref(true);

    // Pagination pour les membres
    const membersPagination = reactive({
        pageNumber: 1,
        total: 0,
        per_page: 10,
        last_page: 1
    });

    const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

    function formatDateFrench(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return `${date.getDate()} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
    }

    function formatAmountFr(amount: number | null | undefined): string {
        if (amount === null || amount === undefined) return '0 F';
        return new Intl.NumberFormat('fr-FR').format(amount) + ' F';
    }

    function calculatePercentage(value: number | null, total: number | null): number {
        if (!value || !total || total === 0) return 0;
        return Math.round((value / total) * 100 * 10) / 10;
    }

    async function fetchCollectiviteDetails() {
        if (!collectivite.value.id) return;
        try {
            loading.value = true;
            const response = await axios.get(`/backoffice/collectivites/get/${collectivite.value.id}`);
            collectivite.value = response.data;
        } catch (error: any) {
            console.error("Erreur lors du chargement de la collectivité:", error);
        } finally {
            loading.value = false;
        }
    }

    async function fetchUsersCount() {
        if (!collectivite.value.id) return;
        try {
            const response = await axios.get(`/backoffice/users/list?collectivite_id=${collectivite.value.id}&pageSize=1`);
            usersCount.value = response.data.total || 0;
        } catch (error: any) {
            console.error("Erreur lors du chargement du nombre de membres:", error);
        }
    }

    async function fetchMembers() {
        if (!collectivite.value.id) return;
        try {
            loadingMembers.value = true;
            const response = await axios.get(`/backoffice/users/list?collectivite_id=${collectivite.value.id}&pageSize=${membersPagination.per_page}&pageNumber=${membersPagination.pageNumber}&verified=1`);
            membersList.value = response.data.data || [];
            membersPagination.total = response.data.total || 0;
            membersPagination.last_page = response.data.last_page || 1;
        } catch (error: any) {
            console.error("Erreur lors du chargement des membres:", error);
        } finally {
            loadingMembers.value = false;
        }
    }

    onMounted(async () => {
        fetchCollectiviteDetails();
        fetchUsersCount();
        fetchMembers();

        // Récupérer les informations de l'utilisateur connecté
        try {
            const response = await axios.get('/backoffice/auth/user');
            currentUser.value = response.data;
        } catch (error) {
            console.error("Erreur lors de la récupération de l'utilisateur:", error);
        }
    });

    function onMembersImported() {
        refreshMembers.value++;
        fetchCollectiviteDetails();
        fetchUsersCount();
        fetchMembers();
    }

    function onMembersPaginate(pageNumber: number) {
        membersPagination.pageNumber = pageNumber;
        fetchMembers();
    }

    async function downloadCsv() {
        try {
            const response = await axios.get("/backoffice/collectivites/membres/download_csv_template", { responseType: "blob" });
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "collectivite_membres_template.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Erreur de téléchargement :", error);
        }
    }

    async function sendInvitationsUnverified(memberId: number) {
        const confirmation = await confirmationModalRef.value?.open(
            "Confirmer l'envoi des invitations",
            `Voulez-vous vraiment envoyer une invitation au membre sélectionné ?`
        );
        if (confirmation) {
            loadingSendInvitation.value[memberId] = true;
            try {
                const response = await axios.post('/backoffice/collectivites/membres/send_invitations_unverified', {
                    users_ids: [memberId],
                    collectivite_id: collectivite.value.id
                });
                toastMessage.value = 'Invitation envoyée avec succès';
                toastType.value = 'success';
                showToast.value = true;
            } catch (error: any) {
                console.error("Erreur lors de l'envoi de l'invitation:", error);
                toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de l'envoi de l'invitation";
                toastType.value = 'error';
                showToast.value = true;
            } finally {
                loadingSendInvitation.value[memberId] = false;
            }
        }
    }

    async function promoteGerant(member: any) {
        if (!collectivite.value.id || !member.id) return;

        try {
            const confirmation = await confirmationModalRef.value?.open(
                "Promouvoir en gérant",
                `Voulez-vous vraiment promouvoir ${member.nom} ${member.prenom} en tant que gérant de cette collectivité ?`
            );

            if (!confirmation) {
                return;
            }

            loadingPromoteGerant.value[member.id] = true;

            const response = await axios.post('/backoffice/collectivites/membres/promote_gerant', {
                user_id: member.id,
                collectivite_id: collectivite.value.id
            });

            toastMessage.value = response.data.message || 'Membre promu gérant avec succès';
            toastType.value = 'success';
            showToast.value = true;

            // Rafraîchir la liste des membres
            setTimeout(() => {
                fetchMembers();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors de la promotion du gérant:", error);
            toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de la promotion";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingPromoteGerant.value[member.id] = false;
        }
    }

    // ----- Gérant : édition / remplacement -----

    function getGerantPhotoUrl(gerant: any): string {
        if (!gerant) return '/assets/users_avatar/600w/user.jpg';
        return gerant.photo_url || gerant.avatar_url || '/assets/users_avatar/600w/user.jpg';
    }

    function resetGerantForm(prefillFrom: any = null) {
        gerantErrors.value = {};
        gerantFormData.nom = prefillFrom?.nom ?? '';
        gerantFormData.prenom = prefillFrom?.prenom ?? '';
        gerantFormData.telephone = prefillFrom?.telephone ?? '';
        gerantFormData.email = prefillFrom?.email ?? '';
        gerantFormData.date_naissance = prefillFrom?.date_naissance ? String(prefillFrom.date_naissance).substring(0, 10) : '';
        gerantFormData.age = prefillFrom?.age ?? 0;
    }

    function recomputeAge() {
        if (!gerantFormData.date_naissance) {
            gerantFormData.age = 0;
            return;
        }
        const dob = new Date(gerantFormData.date_naissance);
        if (isNaN(dob.getTime())) return;
        const now = new Date();
        let age = now.getFullYear() - dob.getFullYear();
        const m = now.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
        gerantFormData.age = age;
    }

    function startGerantEdit() {
        resetGerantForm(collectivite.value.current_gerant);
        gerantMode.value = 'edit';
    }

    async function startGerantReplace() {
        resetGerantForm();
        gerantReplaceMode.value = 'new';
        gerantReplaceExistingId.value = null;
        gerantMode.value = 'replace';

        // Charger les candidats éligibles : membres de cette collectivité (non-gérants)
        // + users sans collectivité. Le backend filtre déjà les gérants existants.
        try {
            const response = await axios.get(`/backoffice/collectivites/${collectivite.value.id}/eligible_gerants`);
            availableUsersForGerant.value = response.data || [];
        } catch (error) {
            console.error("Erreur chargement candidats gérant:", error);
            availableUsersForGerant.value = [];
        }
    }

    function cancelGerantAction() {
        gerantMode.value = 'view';
        gerantErrors.value = {};
    }

    async function resendGerantInvitation() {
        if (!collectivite.value.id) return;
        loadingResendInvitation.value = true;
        try {
            const response = await axios.post(`/backoffice/collectivites/${collectivite.value.id}/gerant/resend-invitation`);
            toastMessage.value = response.data?.message || "Invitation renvoyée avec succès";
            toastType.value = 'success';
            showToast.value = true;
        } catch (error: any) {
            toastMessage.value = error.response?.data?.message || "Erreur lors du renvoi de l'invitation";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingResendInvitation.value = false;
        }
    }

    async function submitGerantEdit() {
        if (!collectivite.value.id) return;
        loadingGerantAction.value = true;
        gerantErrors.value = {};
        try {
            const response = await axios.put(`/backoffice/collectivites/${collectivite.value.id}/gerant`, gerantFormData);
            toastMessage.value = 'Gérant modifié avec succès';
            toastType.value = 'success';
            showToast.value = true;
            gerantMode.value = 'view';
            await fetchCollectiviteDetails();
        } catch (error: any) {
            if (error.response?.status === 422 && error.response?.data?.errors) {
                gerantErrors.value = error.response.data.errors;
            }
            toastMessage.value = error.response?.data?.message || "Erreur lors de la modification du gérant";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingGerantAction.value = false;
        }
    }

    async function submitGerantReplace() {
        if (!collectivite.value.id) return;

        const confirmation = await confirmationModalRef.value?.open(
            "Changer de gérant",
            "Voulez-vous vraiment remplacer le gérant actuel ? L'ancien gérant sera retiré de cette collectivité."
        );
        if (!confirmation) return;

        loadingGerantAction.value = true;
        gerantErrors.value = {};
        try {
            const payload: any = {};
            if (gerantReplaceMode.value === 'existing') {
                if (!gerantReplaceExistingId.value) {
                    gerantErrors.value = { gerant_id: ['Veuillez sélectionner un utilisateur'] };
                    loadingGerantAction.value = false;
                    return;
                }
                payload.gerant_id = gerantReplaceExistingId.value;
            } else {
                payload.gerant = { ...gerantFormData };
            }

            const response = await axios.post(`/backoffice/collectivites/${collectivite.value.id}/gerant/replace`, payload);
            toastMessage.value = 'Gérant remplacé avec succès';
            toastType.value = 'success';
            showToast.value = true;
            gerantMode.value = 'view';
            await fetchCollectiviteDetails();
            fetchMembers();
        } catch (error: any) {
            if (error.response?.status === 422 && error.response?.data?.errors) {
                gerantErrors.value = error.response.data.errors;
            }
            toastMessage.value = error.response?.data?.message || "Erreur lors du remplacement du gérant";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingGerantAction.value = false;
        }
    }

    function getStatusTags(item: any) {
        const tags = [];
        if (item.email_verified_at) {
            tags.push({ label: 'Authentification', type: 'success' });
        } else {
            tags.push({ label: 'Authentification', type: 'warning' });
        }

        if (item.souscription_id) {
            tags.push({ label: 'Souscription', type: 'success' });
        } else {
            tags.push({ label: 'Souscription', type: 'warning' });
        }

        if (item.code_securite) {
            tags.push({ label: 'Code de sécurité', type: 'success' });
        } else {
            tags.push({ label: 'Code de sécurité', type: 'warning' });
        }

        if (item.actif) {
            tags.push({ label: 'Connecter', type: 'success' });
        } else {
            tags.push({ label: 'Connecter', type: 'warning' });
        }

        return tags;
    }

    function goToEdit() {
        router.push(`/app/collectivites/form/${collectivite.value.id}`);
    }

    function goToOnboarding() {
        router.push(`/app/collectivites/onboarding-groupe/${collectivite.value.id}`);
    }

    function goToPrelevements() {
        router.push(`/app/collectivites/${collectivite.value.id}/prelevements`);
    }

    // Vérifier si l'utilisateur est gérant de cette collectivité
    const isGerant = computed(() => {
        if (!currentUser.value || !collectivite.value.id) return false;

        // Vérifier si l'utilisateur est gérant via la table pivot user_collectivites
        const userCollectivites = currentUser.value.collectivites || [];
        const collectiviteGeree = userCollectivites.find(
            (c: any) => c.id === collectivite.value.id && c.pivot?.is_gerant === true
        );

        return !!collectiviteGeree;
    });

    // Computed pour les statistiques
    const stats = computed(() => collectivite.value.stats || {});

    const membersPercentage = computed(() => {
        const maxMembers = 50000; // Valeur par défaut pour la barre de progression
        return Math.min(100, calculatePercentage(stats.value.members_count, maxMembers));
    });

    const prelevementPercentage = computed(() => {
        const current = stats.value.current_prelevement;
        if (!current) return 0;
        return calculatePercentage(current.montant_prelevement, current.montant_prelevement_expected);
    });

    const decaissementPercentage = computed(() => {
        const current = stats.value.current_decaissement;
        if (!current) return 0;
        return calculatePercentage(current.montant_decaissement, current.montant_decaissement_expected);
    });

    const activeBalancePercentage = computed(() => {
        return calculatePercentage(stats.value.active_balance, stats.value.total_balance);
    });

    const blockedBalancePercentage = computed(() => {
        return calculatePercentage(stats.value.blocked_balance, stats.value.total_balance);
    });

    const walletSolde = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        const totalDecaissements = stats.value.total_decaissements || 0;
        return totalPrelevements - totalDecaissements;
    });

    const walletSoldePercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(walletSolde.value, totalPrelevements);
    });

    const decaissementTotalPercentage = computed(() => {
        const totalPrelevements = stats.value.total_prelevements || 0;
        if (totalPrelevements === 0) return 0;
        return calculatePercentage(stats.value.total_decaissements, totalPrelevements);
    });

</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Collectivités / Détails <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <span class="btn shadow-200 white2">Informations</span>
            <span class="btn shadow-200 white2">Wallet / Décaissement</span>
            <span class="btn shadow-200 white2">Gérant</span>
            <span class="btn shadow-200 white2">Membres</span>
        </div>

        <!--Collectivité - Component-->
        <div class="s20 widget px-6 pt-8 pb-1">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isCollectiviteOpen }" @click="isCollectiviteOpen = !isCollectiviteOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Collectivité
                </span>
            </div>
            <div class="s10 mt--5-5 gap-4 flex-right">
                <button class="btn lg py-3 white2 active" @click.stop="goToEdit">
                    <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                    <span>Editer</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="goToOnboarding">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Onboarding</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="goToPrelevements">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Prélèvements / Décaissements</span>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 collapse-content pt-5 pb-8" :class="{ 'active': isCollectiviteOpen }" v-show="isCollectiviteOpen">
                <div class="s3_mobile px-8">
                    <div class="rounded-100 overflow-hidden item active">
                        <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                    </div>
                    <h6 class="font-normal mt-7 mb-4">
                        <b class="text-xl">{{ collectivite.pays?.nom || 'Côte d\'Ivoire' }} | {{ Array.isArray(collectivite.secteurs) ? collectivite.secteurs.join(', ') : (collectivite.secteurs || 'N/A') }}</b>
                    </h6>
                    <h6 class="font-normal mb-4">
                        {{ collectivite.telephone || '' }} {{ collectivite.email ? '| ' + collectivite.email : '' }}<br class="mb-2"/>
                    </h6>
                    <h6 class="font-normal mb-4">
                        Adresse <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.adresse || 'N/A' }}</b>
                    </h6>
                </div>
                <div class="s4_mobile px-2">
                    <h3 class="mb-2">{{ collectivite.nom || '' }}</h3>
                    <h4 class="mb-5">{{ collectivite.rccm || 'N/A' }}</h4>
                    <span class="tag-state notif stroke">Actif</span>

                    <h6 class="font-normal mt-5 mb-4" v-if="collectivite.date_souscription">
                        Date de souscription <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(collectivite.date_souscription) }}</b>
                    </h6>

                    <h6 class="font-normal mt-5 mb-4" v-if="collectivite.date_creation || collectivite.created_at">
                        Date de création <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(collectivite.date_creation || collectivite.created_at) }}</b>
                    </h6>

                    <h6 class="font-normal mb-4" v-if="collectivite.rccm">
                        Numero d'immatriculation <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.rccm }}</b>
                    </h6>
                    <h6 class="font-normal mb-4">
                        Adresse <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.adresse || 'N/A' }}</b>
                    </h6>
                    <hr v-if="collectivite.justificatif"/>
                    <h6 class="font-normal mb-4" v-if="collectivite.justificatif">
                        Justificatif <br class="mb-2"/>
                        <b class="text-xl">{{ collectivite.justificatif }}</b>
                    </h6>
                </div>
                <div class="s13_mobile px-5">
                    <!--Barre de progression - Membres-->
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">Membres {{ stats.members_count ? Math.round((stats.members_count / 50000) * 100) : 0 }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: membersPercentage + '%' }">
                            <div class="text-2xl position-absolute">{{ stats.members_count || 0 }} / 50000</div>
                        </div>
                    </div>
                    <div class="clear-both h-15"></div>

                    <!--Prélèvement du mois courant-->
                    <h3 class="font-normal mb-3">Prélèvement de {{ stats.current_month_label || 'Janvier 2026' }}</h3>
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">{{ prelevementPercentage }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 px-7 bg-pop-green" :style="{ width: prelevementPercentage + '%' }">
                            <h2 class="position-absolute">{{ formatAmountFr(stats.current_prelevement?.montant_prelevement) }} / {{ formatAmountFr(stats.current_prelevement?.montant_prelevement_expected || 1000000) }}</h2>
                        </div>
                    </div>
                    <div class="clear-both h-15"></div>

                    <!--Décaissement du mois courant-->
                    <h3 class="font-normal mb-3">Décaissement de {{ stats.current_month_label || 'Janvier 2026' }}</h3>
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">{{ decaissementPercentage }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 px-7 bg-color-1000" :style="{ width: decaissementPercentage + '%' }">
                            <h2 class="position-absolute text-white">{{ formatAmountFr(stats.current_decaissement?.montant_decaissement) }} / {{ formatAmountFr(stats.current_decaissement?.montant_decaissement_expected || 1000000) }}</h2>
                        </div>
                    </div>
                    <!--End Barre de progression-->

                    <h5 class="secondary-font font-semibold text-primary-color mt-5">
                        Taux de financement
                    </h5>
                    <h1 class="secondary-font font-semibold text-primary-color text-5xl">{{ collectivite.profil?.taux_interet || 0 }}%</h1>
                </div>
                <div class="clear-both h-10"></div>
                <hr class="mb-8"/>

                <!--Statistiques financières-->
                <div class="s20 flex items-center px-8 pb-2 gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Débloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.active_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ activeBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(activeBalancePercentage, 5) + '%' }">
                                {{ activeBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-purple"></div>
                            Bloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.blocked_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ blockedBalancePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" :style="{ width: Math.max(blockedBalancePercentage, 5) + '%' }">
                                {{ blockedBalancePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Total
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">{{ formatAmountFr(stats.total_balance) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--End Collectivité-->

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Wallet - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isWalletOpen }" @click="isWalletOpen = !isWalletOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Wallet / Décaissement
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
            </div>

            <div class="s20 gap-6 px-5 pb-8 collapse-content" :class="{ 'active': isWalletOpen }" v-show="isWalletOpen">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-black"></div>
                            Solde
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(walletSolde) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ walletSoldePercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-black" :style="{ width: Math.max(walletSoldePercentage, 5) + '%' }">
                                {{ walletSoldePercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Montant Décaissé
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.total_decaissements) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">{{ decaissementTotalPercentage }}%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" :style="{ width: Math.max(decaissementTotalPercentage, 5) + '%' }">
                                {{ decaissementTotalPercentage }}%
                            </div>
                        </div>
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Dernier décaissement
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ formatAmountFr(stats.last_decaissement?.montant_decaissement) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <!--End Wallet -->
        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Gérant - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isGerantSectionOpen }" @click="isGerantSectionOpen = !isGerantSectionOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Gérant
                </span>
            </div>
            <div class="s10 mt--5-5 gap-4 flex-right" v-if="gerantMode === 'view' && collectivite.current_gerant">
                <button
                    v-if="!collectivite.current_gerant.last_connnection_at"
                    class="btn lg py-3 white2 active"
                    :disabled="loadingResendInvitation"
                    @click.stop="resendGerantInvitation"
                >
                    <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                    <span v-if="!loadingResendInvitation">Renvoyer l'invitation</span>
                    <span v-else>Envoi en cours...</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="startGerantEdit">
                    <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                    <span>Modifier</span>
                </button>
                <button class="btn lg py-3 white2 active" @click.stop="startGerantReplace">
                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                    <span>Changer de gérant</span>
                </button>
            </div>
            <div class="s10 mt--5-5 gap-4 flex-right" v-else-if="gerantMode === 'view' && !collectivite.current_gerant">
                <button class="btn lg py-3 white2 active" @click.stop="startGerantReplace">
                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                    <span>Désigner un gérant</span>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 collapse-content pt-3 pb-6" :class="{ 'active': isGerantSectionOpen }" v-show="isGerantSectionOpen">
                <!-- Vue : info du gérant actuel -->
                <div v-if="gerantMode === 'view'" class="px-5">
                    <div v-if="collectivite.current_gerant" style="display: flex; align-items: center; gap: 1.5rem;">
                        <div style="width: 160px; height: 160px; min-width: 160px; max-width: 160px; flex: 0 0 160px; border-radius: 15px; overflow: hidden;">
                            <img :src="getGerantPhotoUrl(collectivite.current_gerant)" style="width: 160px; height: 160px; object-fit: cover; display: block;" />
                        </div>
                        <div style="flex: 1 1 auto; min-width: 0;">
                            <h3 class="mb-2">{{ collectivite.current_gerant.nom }} {{ collectivite.current_gerant.prenom }}</h3>
                            <p class="text-xl">{{ collectivite.current_gerant.email }}</p>
                            <p class="text-xl">{{ collectivite.current_gerant.telephone }}</p>
                            <p class="text-xl mt-2" v-if="collectivite.current_gerant.fonction">{{ collectivite.current_gerant.fonction }}</p>
                        </div>
                    </div>
                    <div v-else class="text-grey-500 text-center py-5">
                        Aucun gérant désigné pour cette collectivité.
                    </div>
                </div>

                <!-- Édition / Création (remplacement) : champs -->
                <div v-if="gerantMode === 'edit' || (gerantMode === 'replace' && gerantReplaceMode === 'new')" class="px-5">
                    <h2 class="font-medium text-primary-color my-5">
                        {{ gerantMode === 'edit' ? 'Modifier les infos du gérant' : 'Nouveau gérant' }}
                    </h2>
                    <div class="form-field-floating reactive active my-3">
                        <input type="text" class="form-input" v-model="gerantFormData.nom" placeholder="Nom" />
                        <label>Nom <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors.nom || gerantErrors['gerant.nom']" class="text-red">{{ (gerantErrors.nom || gerantErrors['gerant.nom'])?.[0] }}</span>

                    <div class="form-field-floating reactive active my-3">
                        <input type="text" class="form-input" v-model="gerantFormData.prenom" placeholder="Prénom" />
                        <label>Prénom <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors.prenom || gerantErrors['gerant.prenom']" class="text-red">{{ (gerantErrors.prenom || gerantErrors['gerant.prenom'])?.[0] }}</span>

                    <div class="form-field-floating reactive active my-3">
                        <input type="text" class="form-input" v-model="gerantFormData.telephone" placeholder="Téléphone" />
                        <label>Téléphone <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors.telephone || gerantErrors['gerant.telephone']" class="text-red">{{ (gerantErrors.telephone || gerantErrors['gerant.telephone'])?.[0] }}</span>

                    <div class="form-field-floating reactive active my-3">
                        <input type="email" class="form-input" v-model="gerantFormData.email" placeholder="Email" />
                        <label>Email <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors.email || gerantErrors['gerant.email']" class="text-red">{{ (gerantErrors.email || gerantErrors['gerant.email'])?.[0] }}</span>

                    <div class="form-field-floating reactive active my-3">
                        <input type="date" class="form-input" v-model="gerantFormData.date_naissance" @change="recomputeAge" />
                        <label>Date de naissance <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors['gerant.date_naissance']" class="text-red">{{ gerantErrors['gerant.date_naissance']?.[0] }}</span>

                    <div class="form-field-floating reactive active my-3">
                        <input type="number" class="form-input" v-model.number="gerantFormData.age" readonly />
                        <label>Âge</label>
                    </div>
                    <span v-if="gerantErrors['gerant.age']" class="text-red">{{ gerantErrors['gerant.age']?.[0] }}</span>
                </div>

                <!-- Remplacement : choix existing/new -->
                <div v-if="gerantMode === 'replace'" class="px-5 mb-3">
                    <div class="form-check mb-3">
                        <input type="radio" id="replace_new" value="new" v-model="gerantReplaceMode" />
                        <label for="replace_new" class="ml-2">Créer un nouveau gérant</label>
                    </div>
                    <div class="form-check mb-3">
                        <input type="radio" id="replace_existing" value="existing" v-model="gerantReplaceMode" />
                        <label for="replace_existing" class="ml-2">Promouvoir un utilisateur existant</label>
                    </div>

                    <div v-if="gerantReplaceMode === 'existing'" class="form-field-floating reactive active my-3">
                        <select class="form-input-select" v-model="gerantReplaceExistingId">
                            <option :value="null">— Sélectionnez un utilisateur —</option>
                            <option v-for="u in availableUsersForGerant" :key="u.id" :value="u.id">
                                {{ u.fullname || (u.nom + ' ' + u.prenom) }} ({{ u.email }})
                            </option>
                        </select>
                        <label>Utilisateur existant <b class="color-danger">*</b></label>
                    </div>
                    <span v-if="gerantErrors.gerant_id" class="text-red">{{ gerantErrors.gerant_id?.[0] }}</span>
                </div>

                <!-- Actions -->
                <div v-if="gerantMode !== 'view'" class="flex-right gap-3 px-5 mt-5">
                    <button class="btn lg py-3 white2" @click="cancelGerantAction" :disabled="loadingGerantAction">
                        Annuler
                    </button>
                    <button class="btn lg py-3" @click="gerantMode === 'edit' ? submitGerantEdit() : submitGerantReplace()" :disabled="loadingGerantAction">
                        <span v-if="!loadingGerantAction">Enregistrer</span>
                        <span v-else>Enregistrement...</span>
                    </button>
                </div>
            </div>
        </div>
        <!--End Gérant-->
        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Membre - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open" :class="{ 'active': isMembresOpen }" @click="isMembresOpen = !isMembresOpen" style="cursor: pointer;">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Membres
                </span>
            </div>
         <!--    <div class="s10 mt--5-5 gap-4 flex-right">
                <button type="button" @click.stop="downloadCsv" class="btn lg py-3 white2 active">
                    <img src="/icons/icon-telecharger.svg" class="btn-icon" alt="" />
                    <span>Modèle CSV</span>
                </button>
                <label for="file-upload-membres-details" class="btn lg py-3 white2 active" style="cursor: pointer;" @click.stop>
                    <img src="/icons/icon-upload.svg" class="btn-icon" alt="" />
                    <span>Importer CSV</span>
                </label>
                <button type="button" @click.stop="sendInvitationsUnverified" class="btn lg py-3" :disabled="loadingInvitations">
                    <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                    <span v-if="!loadingInvitations">Envoyer invitations</span>
                    <span v-else>Envoi en cours...</span>
                </button>
            </div> -->
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content" :class="{ 'active': isMembresOpen }" v-show="isMembresOpen">
                <!-- Import CSV Component -->
                <import-membres-component
                    v-if="collectivite.id"
                    :collectivite-id="collectivite.id"
                    :key="refreshMembers"
                    @completed="onMembersImported"
                />
                <div class="clear-both h-5"></div>
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th><label class="btn-circle" for="check-members-all"><input type="checkbox" name="check-members-all" id="check-members-all" /></label></th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th class="flex-right">
                                <button class="btn-ultra color2">
                                    <div class="btn-show">
                                        <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loadingMembers">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    <div class="loader dark"></div>
                            </td>
                        </tr>
                        </template>
                        <template v-else-if="membersList.length === 0">
                            <tr>
                                <td colspan="5" class="text-center text-grey-500 py-5">
                                    Aucun membre trouvé
                            </td>
                        </tr>
                        </template>
                        <tr class="text-grey-700 over-effect" v-for="(member, index) in membersList" :key="'member_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'member_check_' + index">
                                    <input type="checkbox" name="selectedMembers[]" :id="'member_check_' + index" :value="member.id" v-model="selectedMembers" />
                                </label>
                            </td>
                            <td class="flex">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                    <img :src="!!member.photo_url ? member.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                                </div>
                                <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-warning-rgb), 0.2);" v-if="collectivite.logo">
                                    <img :src="BACKEND_URL + '/assets/collectivites/' + collectivite.logo" width="100%" />
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    {{ member.reference || 'N/A' }} &nbsp;
                                    <span class="tag-state warning stroke" v-if="!member.email_verified_at">En cours</span>
                                    <span class="tag-state notif stroke" v-if="member.actif">Actif</span>
                                </p>
                                {{ member.nom + ' ' + member.prenom }} - {{ member.collectivite?.nom || collectivite.nom }}
                                <span class="tag-state success stroke ml-2" v-if="member.is_gerant">Gérant</span>
                            </td>
                            <td class="text-3xl">
                                {{ member.quotite_cessible_pourcent ? member.quotite_cessible_pourcent + '%' : '-' }}
                                <p class="mt-1">{{ member.quotite_cessible_montant ? formatAmountFr(member.quotite_cessible_montant) : '-' }}</p>
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/app/users/form/' + member.id">
                                    <button class="btn tips">
                                        <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                    </button>
                                    </router-link>
                                    <button
                                        @click="promoteGerant(member)"
                                        class="btn-ultra color2 tips"
                                        :disabled="loadingPromoteGerant[member.id] || member.is_gerant"
                                        :title="member.is_gerant ? 'Déjà gérant' : 'Promouvoir en gérant'"
                                    >
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        </div>
                                        <div class="btn-loading" v-if="loadingPromoteGerant[member.id]">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn white2 active" v-if="member.can_receive_invitation_link == true">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Envoyer lien d'invitation</span>
                                    </button>
                                    <button type="button" @click="sendInvitationsUnverified(member.id)" class="btn normal ml-3 justify-content-center btn-filtre" :disabled="loadingSendInvitation[member.id]" v-if="member.can_receive_invitation_link == true">
                                        <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                                        <span v-if="!loadingSendInvitation[member.id]">Envoyer invitation</span>
                                        <span v-else>Envoi...</span>
                                    </button>
                                    <router-link :to="'/app/users/' + member.id + '/details'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Gérer</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/carte'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Carte</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/utilisation'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Utilisation</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/plan'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Plan</span>
                                    </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + member.id + '/revolving'">
                                    <button class="btn white2 active">
                                        <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                        <span>Revolving</span>
                                    </button>
                                    </router-link>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!--Pagination-->
                <div style="display: flex; flex-direction: row; justify-content: center;" v-if="membersList.length > 0">
                    <custom-pagination
                        v-model="membersPagination.pageNumber"
                        :total="membersPagination.total"
                        :per-page="membersPagination.per_page"
                        :last_page="membersPagination.last_page"
                        @update:modelValue="onMembersPaginate"
                    />
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>

        <confirmation-modal ref="confirmationModalRef" />
        <toast-notification
            :message="toastMessage"
            :type="toastType"
            :show="showToast"
            @update:show="showToast = $event"
        />
    </section>
</template>
